import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  Banknote, Building2, Gauge, Gavel, LandPlot, LogOut, Route as RouteIcon, ScrollText, ShieldCheck, Wallet,
} from 'lucide-react';
import { ApiClientError, LogoMark } from '@riskpay/ui-components';
import { SignIn } from './screens/SignIn';
import { Telemetry } from './screens/Telemetry';
import { Merchants } from './screens/Merchants';
import { SettlementEngine } from './screens/SettlementEngine';
import { AuditLog } from './screens/AuditLog';
import { KycControl } from './screens/KycControl';
import { Routes } from './screens/Routes';
import { PayoutAccounts } from './screens/PayoutAccounts';
import { Disputes } from './screens/Disputes';
import { Payouts } from './screens/Payouts';
import { useAuth } from './lib/client';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 20_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) =>
        error instanceof ApiClientError && error.status === 401 ? false : failureCount < 2,
    },
  },
});

type Route =
  | 'telemetry'
  | 'routes'
  | 'kyc'
  | 'payout-accounts'
  | 'merchants'
  | 'settlements'
  | 'payouts'
  | 'disputes'
  | 'audit';

const TABS: { id: Route; label: string; icon: typeof Gauge }[] = [
  { id: 'telemetry', label: 'Telemetry', icon: Gauge },
  { id: 'routes', label: 'Payment Routes', icon: RouteIcon },
  { id: 'kyc', label: 'KYC Control', icon: ShieldCheck },
  { id: 'payout-accounts', label: 'Payout Accounts', icon: LandPlot },
  { id: 'merchants', label: 'Merchants', icon: Building2 },
  { id: 'settlements', label: 'Settlement Engine', icon: Wallet },
  { id: 'payouts', label: 'Payouts', icon: Banknote },
  { id: 'disputes', label: 'Disputes', icon: Gavel },
  { id: 'audit', label: 'Audit Log', icon: ScrollText },
];

/**
 * Quantum.
 *
 * The treasury core. Every screen here can move money or change what a merchant
 * is charged, so the chrome is deliberately sober — a permanent emerald rule at
 * the top marks that this is the live platform, not a sandbox.
 */
function Console() {
  const [route, setRoute] = useState<Route>('telemetry');
  const session = useAuth((s) => s.session);
  const signOut = useAuth((s) => s.signOut);

  return (
    <div className="min-h-screen">
      <div className="fixed inset-x-0 top-0 z-[60] h-0.5 bg-brand-500" aria-hidden="true" />

      <header className="sticky top-0 z-30 border-b border-line bg-surface/90 backdrop-blur-md">
        <div className="flex h-[74px] items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <LogoMark size={46} />
            <div className="leading-none">
              <p className="text-lg font-semibold tracking-tight">Quantum</p>
              <p className="mt-1.5 text-xs text-content-subtle">Treasury Core · live platform</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <p className="text-xs text-content">{session?.user.email}</p>
              <p className="text-2xs uppercase tracking-wider text-content-faint">{session?.user.role}</p>
            </div>
            <button type="button" onClick={signOut} className="btn btn-secondary h-8 text-xs">
              <LogOut className="h-3.5 w-3.5" />
              Sign out
            </button>
          </div>
        </div>

        <nav className="flex gap-1 overflow-x-auto px-4 sm:px-6" aria-label="Sections">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.id === route;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setRoute(tab.id)}
                aria-current={isActive ? 'page' : undefined}
                className={`relative flex shrink-0 items-center gap-2 px-3.5 py-3 text-[15px] transition-colors ${
                  isActive ? 'text-content' : 'text-content-muted hover:text-content'
                }`}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                {tab.label}
                {isActive && <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-brand-500" />}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="px-4 py-6 sm:px-6">
        {route === 'telemetry' && <Telemetry />}
        {route === 'routes' && <Routes />}
        {route === 'kyc' && <KycControl />}
        {route === 'payout-accounts' && <PayoutAccounts />}
        {route === 'merchants' && <Merchants />}
        {route === 'settlements' && <SettlementEngine />}
        {route === 'payouts' && <Payouts />}
        {route === 'disputes' && <Disputes />}
        {route === 'audit' && <AuditLog />}
      </main>
    </div>
  );
}

export function App() {
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  return (
    <QueryClientProvider client={queryClient}>
      {isAuthenticated ? <Console /> : <SignIn />}
    </QueryClientProvider>
  );
}
